"use client";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

// Exported so pages/products/riskintel can emit FAQPage schema from the same copy.
export const FAQS = [
  {
    q: "We already screen names internally. What does RiskIntel actually add?",
    a: "Most internal screening hits global sanctions lists and stops there. RiskIntel adds the 13-register Indian regulatory stack (UAPA, MHA, MCA, ED, SEBI, IBBI, RBI, NIA, CBI, BSE, NSE, DGARM, MyNeta and more, totalling 300K+ entities), live eCourts coverage across every Indian state and union territory, live GSTIN verification, and the director-network mapping that surfaces co-director and shareholder paths to sanctioned and PEP entities no global tool will tell you about.",
  },
  {
    q: "How fresh is the Indian regulatory data?",
    a: "Daily refresh for sanctions and wanted lists (MHA, UAPA, CBI). Monthly for MCA Struck-off Companies and MCA Disqualified Directors. Live (request-time) for GSTIN verification and eCourts queries. Every dataset shows its last-refreshed timestamp on the match card.",
  },
  {
    q: "Do you support our existing KYC pipeline via API?",
    a: "Yes. REST API with an OpenAPI 3 spec, per-key tokens (self-service mint and revoke), enforced rate limits on every key, and per-key audit logs. Drop a single screening call into your onboarding flow, or bulk-screen 10K rows from a CSV with live progress and a downloadable export that survives session loss.",
  },
  {
    q: "How long does setup take?",
    a: "For the web app, your team starts screening live during the demo call. We walk you through tenant setup, role assignment, and your first real screenings, so you leave the call with the platform already in use. For the API, your developers can quickly wire a screening call into your existing onboarding or KYC pipeline. We stay on the call for the first integration.",
  },
  {
    q: "Can we trial it before paying?",
    a: "Yes. After the demo, your team gets a sandbox tenant with full coverage, real datasets, and your real screening data. You run it against your actual queue, see the deduplication, network paths, and audit trail in your own workflow, and decide whether it earns a place in your stack.",
  },
  {
    q: "What happens if I get a false positive?",
    a: "Alias clustering and strong-ID anchoring (PAN, DIN, passport) prevent most same-name false merges before they reach an analyst. When a borderline case appears, the match card shows every corroborating signal: PAN, DIN, DOB, father's name, address, datasets, and the risk path, so an analyst can review the evidence and bookmark the decision to Saved Reports for the audit trail. Every action is logged with user, timestamp, dataset, and resource details, ensuring the compliance review remains defensible.",
  },
  {
    q: "How secure is it? Where is our data hosted?",
    a: "Data hosted in India. Encryption in transit and at rest. SOC 2-aligned controls. Tenant-scoped data isolation at the architecture level. Role-based access (Viewer / Analyst / Admin / Owner). Every action logged with user, timestamp, and resource.",
  },
];

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="section-pad bg-surface">
      <div className="container-c">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue">FAQ</p>
          <h2 className="mt-3 font-display text-[32px] leading-[1.08] tracking-tight text-balance text-ink sm:text-[40px] md:text-[44px]">
            Frequently asked questions.
          </h2>
        </div>

        <div className="mx-auto mt-12 max-w-3xl space-y-3">
          {FAQS.map((faq, i) => {
            const open = openIndex === i;
            return (
              <div
                key={faq.q}
                className="group rounded-2xl border border-line bg-white p-5"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 text-left"
                  aria-expanded={open}
                >
                  <span className="font-display text-[16px] font-semibold text-ink md:text-[17px]">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-blue transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                  />
                </button>
                {open && (
                  <p className="mt-4 text-[15px] leading-relaxed text-muted">
                    {faq.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
